import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, HelpCircle, Loader2, RotateCcw } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { Section, AidNotice } from "../components/site/blocks";
import { Field, inputProps, Honeypot, Consent, CONSENT_MSG } from "../components/site/FormField";
import { api, formatApiError, EMAIL_RE, PHONE_RE, CP_RE } from "../lib/api";

const INITIAL = { project: "", occupancy: "", age: "", gir: false, handicap: false, housing_over_15: "", dpe: "inconnu", region: "", household_size: "1", rfr: "" };
const fmt = (n) => n.toLocaleString("fr-FR") + " €";

const Choice = ({ name, value, current, onChange, label, desc }) => (
  <label className={`cursor-pointer rounded-2xl border-2 p-5 transition-colors block ${current === value ? "border-navy bg-mist" : "border-slate-200 hover:border-navy/40"}`}>
    <input type="radio" className="sr-only" name={name} value={value} checked={current === value} onChange={() => onChange(value)} data-testid={`sim-${name}-${value}`} />
    <span className="block font-semibold text-navy text-lg">{label}</span>
    {desc && <span className="block text-[15px] text-slate-600 mt-1">{desc}</span>}
  </label>
);

const Group = ({ legend, error, children, id }) => (
  <fieldset className="space-y-3" aria-describedby={error ? `${id}-error` : undefined}>
    <legend className="font-semibold text-navy text-lg mb-3">{legend}</legend>
    {children}
    {error && <p id={`${id}-error`} role="alert" data-testid={`error-${id}`} className="text-red-700 font-medium">{error}</p>}
  </fieldset>
);

const STEP_FIELDS = [["project", "occupancy"], ["age", "housing_over_15"], ["region", "household_size", "rfr"]];

const validateStep = (s, a) => {
  const e = {};
  if (s === 0) {
    if (!a.project) e.project = "Merci de choisir un type de projet.";
    if (!a.occupancy) e.occupancy = "Merci d'indiquer votre statut.";
  }
  if (s === 1) {
    const age = Number(a.age);
    if (!a.age || !Number.isInteger(age) || age < 18 || age > 120) e.age = "Merci d'indiquer un âge valide.";
    if (!a.housing_over_15) e.housing_over_15 = "Merci de répondre à cette question.";
  }
  if (s === 2) {
    if (!a.region) e.region = "Merci de choisir votre région.";
    const n = Number(a.household_size);
    if (!Number.isInteger(n) || n < 1 || n > 20) e.household_size = "Nombre de personnes invalide.";
    if (a.rfr === "" || Number(a.rfr) < 0 || Number.isNaN(Number(a.rfr))) e.rfr = "Merci d'indiquer votre revenu fiscal de référence.";
  }
  return e;
};

const STATUS_UI = {
  eligible: { icon: CheckCircle2, cls: "border-green-700/30 bg-green-50", ic: "text-green-700", label: "Éligibilité probable" },
  a_verifier: { icon: HelpCircle, cls: "border-amber-700/30 bg-amber-50", ic: "text-amber-700", label: "À vérifier" },
  non_eligible: { icon: AlertTriangle, cls: "border-red-700/25 bg-red-50", ic: "text-red-700", label: "Non éligible" },
};

const ResultView = ({ result, onContact, onRestart }) => (
  <div data-testid="sim-result" className="space-y-6">
    <div className="rounded-[24px] bg-navy text-mist p-7 sm:p-9">
      <p className="text-mist/80">Votre catégorie de revenus (barème Anah 2026, {result.region_label})</p>
      <p className="mt-2 font-display text-4xl" data-testid="sim-result-category">Revenus {result.category_label}</p>
      <p className="mt-4 text-mist/85 text-[15px]">Plafonds pour votre foyer : très modestes ≤ {fmt(result.ceilings.tm)} · modestes ≤ {fmt(result.ceilings.m)} · intermédiaires ≤ {fmt(result.ceilings.i)}</p>
    </div>
    {result.aids.map((a) => {
      const ui = STATUS_UI[a.status];
      return (
        <article key={a.key} data-testid={`sim-aid-${a.key}`} data-status={a.status} className={`rounded-2xl border-2 p-6 ${ui.cls}`}>
          <div className="flex items-start gap-3">
            <ui.icon className={`h-7 w-7 shrink-0 ${ui.ic}`} aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-bold uppercase tracking-wider text-slate-700">{a.name} — {ui.label}</p>
              <h3 className="mt-1 font-display text-2xl text-navy" data-testid={`sim-aid-${a.key}-headline`}>{a.headline}</h3>
              {a.amount_text && <p className="mt-3 font-semibold text-navy" data-testid={`sim-aid-${a.key}-amount`}>{a.amount_text}</p>}
              <ul className="mt-3 space-y-2 list-disc pl-5 text-slate-800">{a.details.map((d) => <li key={d}>{d}</li>)}</ul>
              {a.status === "non_eligible" && (
                <button type="button" onClick={onContact} className="btn-primary mt-5" data-testid={`sim-aid-${a.key}-contact-cta`}>Être contacté par un conseiller</button>
              )}
            </div>
          </div>
        </article>
      );
    })}
    <p className="rounded-xl bg-mist p-4 text-slate-800 font-medium" data-testid="sim-disclaimer">{result.disclaimer}</p>
    <div className="flex flex-wrap gap-3">
      <button type="button" onClick={onContact} className="btn-accent" data-testid="sim-contact-cta">Recevoir un accompagnement</button>
      <button type="button" onClick={onRestart} className="btn-outline" data-testid="sim-restart"><RotateCcw className="h-4 w-4" aria-hidden="true" /> Refaire une simulation</button>
    </div>
  </div>
);

const SimContactForm = ({ answers, formRef }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", postal_code: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (f.name.trim().length < 2) e.name = "Merci d'indiquer votre nom.";
    if (!EMAIL_RE.test(f.email.trim())) e.email = "Adresse email invalide.";
    if (f.phone.trim() && !PHONE_RE.test(f.phone.trim())) e.phone = "Numéro invalide (exemple : 06 12 34 56 78).";
    if (!CP_RE.test(f.postal_code.trim())) e.postal_code = "Code postal à 5 chiffres requis.";
    if (!f.consent) e.consent = CONSENT_MSG;
    setErrors(e);
    if (Object.keys(e).length) return;
    setState("loading");
    try {
      await api.post("/simulator/lead", { ...f, phone: f.phone.trim() || null, answers });
      setState("done");
    } catch (err) { setServerError(formatApiError(err.response?.data?.detail)); setState("idle"); }
  };
  if (state === "done") return (
    <div ref={formRef} role="status" data-testid="sim-contact-success" className="rounded-2xl bg-mist p-8">
      <CheckCircle2 className="h-10 w-10 text-green-700" aria-hidden="true" />
      <p className="mt-4 font-display text-2xl text-navy">Merci, votre demande a bien été envoyée. Nous vous recontactons sous 48 h ouvrées.</p>
    </div>
  );
  return (
    <form ref={formRef} noValidate onSubmit={submit} data-testid="sim-contact-form" className="relative rounded-2xl border border-navy/10 bg-white p-6 sm:p-8 space-y-5 scroll-mt-32">
      <h2 className="text-2xl">Être recontacté par un conseiller</h2>
      <p className="text-slate-700">Nous vérifions votre situation avec vous et vous présentons les solutions possibles, y compris si vous n'êtes pas éligible aux aides nationales.</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="name" label="Nom et prénom" required error={errors.name}><input {...inputProps("name", errors)} value={f.name} onChange={set("name")} autoComplete="name" /></Field>
        <Field id="email" label="Email" required error={errors.email}><input {...inputProps("email", errors)} type="email" value={f.email} onChange={set("email")} autoComplete="email" /></Field>
        <Field id="phone" label="Téléphone (facultatif)" error={errors.phone}><input {...inputProps("phone", errors)} type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" /></Field>
        <Field id="postal_code" label="Code postal" required error={errors.postal_code}><input {...inputProps("postal_code", errors)} inputMode="numeric" maxLength={5} value={f.postal_code} onChange={set("postal_code")} /></Field>
      </div>
      <Honeypot value={f.website} onChange={set("website")} />
      <Consent checked={f.consent} onChange={set("consent")} error={errors.consent} />
      {serverError && <p role="alert" className="rounded-xl bg-red-50 text-red-800 p-4">{serverError}</p>}
      <button type="submit" className="btn-accent" disabled={state === "loading"} data-testid="sim-contact-submit">
        {state === "loading" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Envoyer
      </button>
    </form>
  );
};

export default function Simulateur() {
  useSeo("Simulateur d'éligibilité MaPrimeAdapt' et MaPrimeRénov'", "Estimez en 2 minutes votre catégorie de revenus Anah 2026 et votre éligibilité indicative à MaPrimeAdapt', MaPrimeRénov' et aux primes CEE.");
  const [step, setStep] = useState(0);
  const [a, setA] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [apiError, setApiError] = useState("");
  const contactRef = useRef(null);
  const set = (k) => (v) => setA((p) => ({ ...p, [k]: v }));
  const payload = () => ({ ...a, age: Number(a.age), household_size: Number(a.household_size), rfr: Number(a.rfr), gir: Number(a.age) >= 60 && Number(a.age) < 70 ? a.gir : false });

  const next = async () => {
    const e = validateStep(step, a);
    setErrors(e);
    if (Object.keys(e).length) return;
    if (step < 2) return setStep(step + 1);
    setLoading(true); setApiError("");
    try {
      const r = await api.post("/simulator/compute", payload());
      setResult(r.data); setStep(3);
    } catch (err) { setApiError(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };
  const openContact = () => { setShowContact(true); setTimeout(() => contactRef.current?.scrollIntoView({ behavior: "smooth" }), 50); };
  const restart = () => { setA(INITIAL); setResult(null); setStep(0); setShowContact(false); };
  const age = Number(a.age);
  const needsEnergy = a.project === "energie" || a.project === "les_deux";

  return (
    <Section className="bg-mist-50" testid="eligibility-simulator">
      <div className="max-w-3xl">
        <p className="eyebrow">Simulateur d'éligibilité</p>
        <h1 className="mt-4 text-4xl sm:text-5xl leading-tight">Quelles aides pour votre projet ?</h1>
        <p className="mt-5 text-lg text-slate-700">Trois questions pour estimer votre catégorie de revenus (barèmes Anah 2026) et les aides vraisemblablement mobilisables. Aucune donnée n'est enregistrée tant que vous ne demandez pas à être recontacté.</p>
        <AidNotice className="mt-6" />
      </div>
      <div className="mt-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {step < 3 && (
            <div className="rounded-[28px] bg-white border border-navy/10 p-6 sm:p-10 shadow-[0_30px_60px_-40px_rgba(16,36,62,0.45)]">
              <div className="flex items-center gap-3 mb-8" aria-label={`Étape ${step + 1} sur 3`}>
                {[0, 1, 2].map((i) => <span key={i} className={`h-2 flex-1 rounded-full ${i <= step ? "bg-brand-ink" : "bg-slate-200"}`} />)}
                <span className="text-sm font-semibold text-slate-600 whitespace-nowrap" data-testid="sim-step-indicator">Étape {step + 1} / 3</span>
              </div>
              {step === 0 && (
                <div className="space-y-8">
                  <Group legend="Quel est votre projet ?" error={errors.project} id="project">
                    <div className="grid sm:grid-cols-3 gap-3">
                      <Choice name="project" value="adaptation" current={a.project} onChange={set("project")} label="Adaptation" desc="Autonomie, salle de bain, accessibilité" />
                      <Choice name="project" value="energie" current={a.project} onChange={set("project")} label="Rénovation énergétique" desc="Isolation, chauffage…" />
                      <Choice name="project" value="les_deux" current={a.project} onChange={set("project")} label="Les deux" />
                    </div>
                  </Group>
                  <Group legend="Vous êtes…" error={errors.occupancy} id="occupancy">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Choice name="occupancy" value="proprietaire_occupant" current={a.occupancy} onChange={set("occupancy")} label="Propriétaire occupant" />
                      <Choice name="occupancy" value="locataire_prive" current={a.occupancy} onChange={set("occupancy")} label="Locataire (parc privé)" />
                      <Choice name="occupancy" value="locataire_social" current={a.occupancy} onChange={set("occupancy")} label="Locataire (logement social)" />
                      <Choice name="occupancy" value="proprietaire_bailleur" current={a.occupancy} onChange={set("occupancy")} label="Propriétaire bailleur" />
                      <Choice name="occupancy" value="autre" current={a.occupancy} onChange={set("occupancy")} label="Autre situation" desc="Usufruitier, hébergé…" />
                    </div>
                  </Group>
                </div>
              )}
              {step === 1 && (
                <div className="space-y-8">
                  <Field id="age" label="Âge de la personne concernée par le projet" required error={errors.age}>
                    <input {...inputProps("age", errors)} type="number" min={18} max={120} inputMode="numeric" value={a.age} onChange={(e) => set("age")(e.target.value)} className={`${inputProps("age", errors).className} max-w-[200px]`} />
                  </Field>
                  {age >= 60 && age < 70 && (
                    <label className="flex gap-3 items-start cursor-pointer">
                      <input type="checkbox" checked={a.gir} onChange={(e) => set("gir")(e.target.checked)} className="mt-1 h-6 w-6 accent-[#2E5683]" data-testid="sim-gir" />
                      <span className="text-slate-800">Une perte d'autonomie a été évaluée (grille GIR, de 1 à 6)</span>
                    </label>
                  )}
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input type="checkbox" checked={a.handicap} onChange={(e) => set("handicap")(e.target.checked)} className="mt-1 h-6 w-6 accent-[#2E5683]" data-testid="sim-handicap" />
                    <span className="text-slate-800">Situation de handicap : taux d'incapacité d'au moins 50 % ou éligibilité à la PCH</span>
                  </label>
                  <Group legend="Le logement a-t-il été construit il y a plus de 15 ans ?" error={errors.housing_over_15} id="housing_over_15">
                    <div className="grid grid-cols-3 gap-3">
                      <Choice name="housing_over_15" value="oui" current={a.housing_over_15} onChange={set("housing_over_15")} label="Oui" />
                      <Choice name="housing_over_15" value="non" current={a.housing_over_15} onChange={set("housing_over_15")} label="Non" />
                      <Choice name="housing_over_15" value="inconnu" current={a.housing_over_15} onChange={set("housing_over_15")} label="Je ne sais pas" />
                    </div>
                  </Group>
                  {needsEnergy && (
                    <Group legend="Étiquette énergétique (DPE) du logement" id="dpe">
                      <div className="grid sm:grid-cols-3 gap-3">
                        <Choice name="dpe" value="efg" current={a.dpe} onChange={set("dpe")} label="E, F ou G" />
                        <Choice name="dpe" value="abcd" current={a.dpe} onChange={set("dpe")} label="A, B, C ou D" />
                        <Choice name="dpe" value="inconnu" current={a.dpe} onChange={set("dpe")} label="Je ne sais pas" />
                      </div>
                    </Group>
                  )}
                </div>
              )}
              {step === 2 && (
                <div className="space-y-8">
                  <Group legend="Où se situe le logement ?" error={errors.region} id="region">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Choice name="region" value="idf" current={a.region} onChange={set("region")} label="Île-de-France" desc="75, 77, 78, 91, 92, 93, 94, 95" />
                      <Choice name="region" value="hors_idf" current={a.region} onChange={set("region")} label="Autre région" />
                    </div>
                  </Group>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field id="household_size" label="Nombre de personnes dans le foyer" required error={errors.household_size}>
                      <input {...inputProps("household_size", errors)} type="number" min={1} max={20} value={a.household_size} onChange={(e) => set("household_size")(e.target.value)} />
                    </Field>
                    <Field id="rfr" label="Revenu fiscal de référence (€)" required error={errors.rfr} hint="Total des avis d'imposition des occupants (avis 2025 en principe).">
                      <input {...inputProps("rfr", errors)} type="number" min={0} inputMode="numeric" value={a.rfr} onChange={(e) => set("rfr")(e.target.value)} />
                    </Field>
                  </div>
                </div>
              )}
              {apiError && <p role="alert" className="mt-6 rounded-xl bg-red-50 text-red-800 p-4">{apiError}</p>}
              <div className="mt-10 flex flex-wrap gap-3 justify-between">
                <button type="button" className="btn-outline" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="sim-prev"><ArrowLeft className="h-5 w-5" aria-hidden="true" /> Précédent</button>
                <button type="button" className="btn-accent" onClick={next} disabled={loading} data-testid="sim-next">
                  {loading && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}{step === 2 ? "Voir mon résultat" : "Suivant"} <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
          {step === 3 && result && <ResultView result={result} onContact={openContact} onRestart={restart} />}
          {step === 3 && showContact && <div className="mt-8"><SimContactForm answers={payload()} formRef={contactRef} /></div>}
        </div>
        <aside className="lg:col-span-4 space-y-5">
          <div className="card-soft">
            <h2 className="text-xl">Comment ça marche ?</h2>
            <p className="mt-3 text-slate-700 text-[15px]">Nous comparons votre revenu fiscal de référence aux plafonds publiés par l'Anah pour 2026, selon la taille de votre foyer et votre région, puis appliquons les principales conditions de MaPrimeAdapt' et MaPrimeRénov'.</p>
          </div>
          <div className="card-soft">
            <h2 className="text-xl">Un doute ?</h2>
            <p className="mt-3 text-slate-700 text-[15px]">Certaines situations demandent une analyse au cas par cas. Un conseiller peut vérifier avec vous.</p>
            <Link to="/contact?type=rappel" className="btn-outline mt-4" data-testid="sim-callback-link">Être rappelé</Link>
          </div>
        </aside>
      </div>
    </Section>
  );
}
